import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import StepIndicator from '../components/launcher/StepIndicator.jsx'
import KindCard from '../components/launcher/KindCard.jsx'
import ClientStep from '../components/launcher/ClientStep.jsx'
import ConsentStep from '../components/launcher/ConsentStep.jsx'
import OverQuotaModal from '../components/shell/OverQuotaModal.jsx'
import Button from '../components/ui/Button.jsx'
import { useAuth } from '../contexts/AuthContext.jsx'

// All kinds now go through the client step. For Découverte, client linking
// is optional (canonically a first meeting with a prospect who doesn't yet
// exist as a Client row), but we still want the advisor to have the choice
// to register them or pick an existing record before recording — same UX
// as the other three kinds.
const STEPS_PER_KIND = {
  decouverte:     ['kind', 'client', 'consent'],
  bilan_annuel:   ['kind', 'client', 'consent'],
  suivi:          ['kind', 'client', 'consent'],
  recommandation: ['kind', 'client', 'consent'],
}

const KINDS = ['decouverte', 'bilan_annuel', 'suivi', 'recommandation']

export default function LauncherPage() {
  const nav = useNavigate()
  const { usage, refreshUsage } = useAuth()
  const [stepIdx, setStepIdx] = useState(0)
  const [data, setData] = useState({ kind: '', client_id: '', client_name: '', pre_seed_facts: null, consent_given: false, consent_method: 'verbal' })

  useEffect(() => { refreshUsage?.() }, [refreshUsage])

  const overQuota = !!(usage && usage.monthly_sessions_quota > 0 &&
    usage.monthly_session_count >= usage.monthly_sessions_quota)

  const flow = data.kind ? STEPS_PER_KIND[data.kind] : ['kind']
  const stepKey = flow[stepIdx]

  const canProceed = (() => {
    if (overQuota) return false
    if (stepKey === 'kind') return !!data.kind
    // Découverte allows starting without a client (anonymous prospect path);
    // every other kind requires one (server-side check would otherwise reject
    // the WS handshake with code 4003).
    if (stepKey === 'client') return !!data.client_id || data.kind === 'decouverte'
    if (stepKey === 'consent') return data.consent_given
    return false
  })()

  const next = () => {
    if (overQuota) return
    if (stepIdx < flow.length - 1) {
      setStepIdx(stepIdx + 1)
      return
    }
    sessionStorage.setItem('clarity_pending_launch', JSON.stringify(data))
    nav('/sessions/live/pending', { replace: true })
  }

  return (
    <div className="mx-auto max-w-[680px] px-4 py-6 sm:px-12 sm:py-8 lg:py-14">
      <StepIndicator step={stepIdx} />
      <h1 className="mb-1.5 mt-8 text-[24px] font-semibold tracking-[-0.02em]">Nouvelle séance</h1>
      <p className="mb-8 text-[14px] text-muted">
        {stepKey === 'kind' && "Choisissez le type — ça oriente l'extraction et le rapport généré."}
        {stepKey === 'client' && (data.kind === 'decouverte'
          ? 'Choisissez ou créez un client — ou continuez en prospect anonyme.'
          : 'Identifiez le client (un nom suffit pour démarrer).')}
        {stepKey === 'consent' && "Confirmez le consentement avant l'enregistrement."}
      </p>

      {stepKey === 'kind' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {KINDS.map(k => <KindCard key={k} kind={k} selected={data.kind === k} onClick={(kind) => setData({ ...data, kind })} />)}
        </div>
      )}
      {stepKey === 'client' && <ClientStep value={data} onChange={setData} />}
      {stepKey === 'consent' && <ConsentStep value={data} onChange={setData} />}

      <div className="mt-8 flex justify-end">
        <Button size="lg" disabled={!canProceed} onClick={next}>
          {stepIdx === flow.length - 1 ? 'Commencer' : 'Continuer'}
          <ArrowRight size={14} strokeWidth={2} />
        </Button>
      </div>

      {overQuota && (
        <OverQuotaModal usage={usage} onClose={() => nav('/')} />
      )}
    </div>
  )
}
